/**
 * QueueRepository - Data access layer for queue management
 * Handles call_queues, agent_queue_assignments, phone_queue_assignments
 * 
 * Part of the 3-layer architecture refactor
 * Repository → Service → React Query Hooks
 */

import { BaseRepository, RepositoryError } from './BaseRepository';

interface QueueFilters {
  is_active?: boolean;
  company_id?: string;
  minPriority?: number;
}

interface QueueWithStats extends CallQueue {
  agent_count?: number;
  phone_count?: number;
}

export interface CallQueue {
  id: string;
  name: string;
  company_name: string;
  company_id: string | null;
  description?: string | null;
  max_wait_time: number;
  priority: number;
  is_active: boolean;
  // New routing policy fields (nullable to avoid breaking existing rows)
  routing_default_mode?: 'human_first' | 'human_only' | 'ai_only' | 'direct_agent' | null;
  default_fallback_ai_agent_id?: string | null;
  default_fallback_timeout_seconds?: number | null;
  direct_agent_id?: string | null;
  ring_strategy?: 'simultaneous' | 'round_robin' | null;
  created_at: string;
  updated_at: string;
}

export interface AgentQueueAssignment {
  id: string;
  agent_id: string;
  queue_id: string;
  priority: number;
  created_at: string;
}

export interface PhoneQueueAssignment {
  id: string;
  phone_number: string;
  queue_id: string;
  is_primary: boolean;
  company_id: string | null;
  // Per-number overrides (all optional)
  routing_mode?: 'human_first' | 'human_only' | 'ai_only' | 'direct_agent' | null;
  fallback_ai_agent_id?: string | null;
  fallback_timeout_seconds?: number | null;
  direct_agent_id?: string | null;
  created_at: string;
}

/**
 * Queue-level voicemail settings
 */
export interface QueueVoicemailSettings {
  queue_id: string;
  enabled: boolean;
  greeting_tts?: string | null;
  greeting_url?: string | null;
  transcribe: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpsertQueueVoicemailSettingsInput {
  queue_id: string;
  enabled?: boolean;
  greeting_tts?: string | null;
  greeting_url?: string | null;
  transcribe?: boolean;
}

export interface CallCenterAgent {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  sip_username: string | null;
  current_status: string;
  created_at: string;
  updated_at: string;
}

/**
 * Repository for managing call queues and assignments
 */
export class QueueRepository extends BaseRepository<CallQueue> {
  constructor() {
    super({
      tableName: 'call_queues',
      primaryKey: 'id'
    });
  }

  /**
   * Get all queues with optional filtering
   */
  async findQueues(filters?: QueueFilters): Promise<CallQueue[]> {
    let query = this.client.from(this.config.tableName).select('*');

    if (filters?.is_active !== undefined) {
      query = query.eq('is_active', filters.is_active);
    }

    if (filters?.company_id) {
      query = query.eq('company_id', filters.company_id);
    }

    if (filters?.minPriority !== undefined) {
      query = query.gte('priority', filters.minPriority);
    }

    query = query.order('priority', { ascending: false });

    const { data, error } = await query;
    if (error) throw new RepositoryError('Failed to fetch queues', error);
    return (data || []) as CallQueue[];
  }

  /**
   * Get queue by ID with related data
   */
  async findQueueWithDetails(queueId: string): Promise<QueueWithStats | null> {
    const { data: queue, error: queueError } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('id', queueId)
      .single();

    if (queueError) {
      if (queueError.code === 'PGRST116') return null;
      throw new RepositoryError('Failed to fetch queue details', queueError);
    }
    if (!queue) return null;

    // Get agent count
    const { count: agentCount } = await this.client
      .from('agent_queue_assignments')
      .select('*', { count: 'exact', head: true })
      .eq('queue_id', queueId);

    // Get phone count
    const { count: phoneCount } = await this.client
      .from('phone_queue_assignments')
      .select('*', { count: 'exact', head: true })
      .eq('queue_id', queueId);

    return {
      ...(queue as CallQueue),
      agent_count: agentCount || 0,
      phone_count: phoneCount || 0
    };
  }

  /**
   * Create a new queue
   */
  async createQueue(queue: Partial<CallQueue>): Promise<CallQueue> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .insert(queue)
      .select()
      .single();

    if (error) throw new RepositoryError('Failed to create queue', error);
    return data as CallQueue;
  }

  /**
   * Update queue configuration
   */
  async updateQueue(queueId: string, updates: Partial<CallQueue>): Promise<CallQueue> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .update(updates)
      .eq('id', queueId)
      .select()
      .single();

    if (error) throw new RepositoryError('Failed to update queue', error);
    return data as CallQueue;
  }

  /**
   * Update routing policy fields for a queue
   */
  async updateQueueRoutingPolicy(
    queueId: string,
    policy: {
      routing_default_mode?: 'human_first' | 'human_only' | 'ai_only' | 'direct_agent' | null;
      default_fallback_ai_agent_id?: string | null;
      default_fallback_timeout_seconds?: number | null;
      direct_agent_id?: string | null;
      ring_strategy?: 'simultaneous' | 'round_robin' | null;
    }
  ): Promise<CallQueue> {
    return this.updateQueue(queueId, policy);
  }

  // ==================== VOICEMAIL SETTINGS ====================

  /**
   * Get queue voicemail settings
   */
  async getQueueVoicemailSettings(queueId: string): Promise<QueueVoicemailSettings | null> {
    const { data, error } = await this.client
      .from('queue_voicemail_settings')
      .select('*')
      .eq('queue_id', queueId)
      .maybeSingle();

    if (error) throw new RepositoryError('Failed to fetch voicemail settings', error);
    return (data as QueueVoicemailSettings) || null;
  }

  /**
   * Upsert queue voicemail settings
   */
  async upsertQueueVoicemailSettings(
    input: UpsertQueueVoicemailSettingsInput
  ): Promise<QueueVoicemailSettings> {
    const { data, error } = await this.client
      .from('queue_voicemail_settings')
      .upsert(
        {
          queue_id: input.queue_id,
          enabled: input.enabled ?? false,
          greeting_tts: input.greeting_tts ?? null,
          greeting_url: input.greeting_url ?? null,
          transcribe: input.transcribe ?? false
        },
        { onConflict: 'queue_id' }
      )
      .select()
      .single();

    if (error) throw new RepositoryError('Failed to upsert voicemail settings', error);
    return data as QueueVoicemailSettings;
  }

  /**
   * Toggle queue active status
   */
  async toggleQueueStatus(queueId: string): Promise<CallQueue> {
    // First get current status
    const { data: current, error: fetchError } = await this.client
      .from(this.config.tableName)
      .select('is_active')
      .eq('id', queueId)
      .single();

    if (fetchError) throw new RepositoryError('Failed to fetch queue status', fetchError);

    // Toggle it
    const { data, error } = await this.client
      .from(this.config.tableName)
      .update({ is_active: !current.is_active })
      .eq('id', queueId)
      .select()
      .single();

    if (error) throw new RepositoryError('Failed to toggle queue status', error);
    return data as CallQueue;
  }

  /**
   * Delete a queue (will fail if there are dependencies)
   */
  async deleteQueue(queueId: string): Promise<void> {
    const { error } = await this.client
      .from(this.config.tableName)
      .delete()
      .eq('id', queueId);

    if (error) throw new RepositoryError('Failed to delete queue', error);
  }

  // ==================== AGENT ASSIGNMENTS ====================

  /**
   * Get all agent assignments for a queue
   */
  async getQueueAgents(queueId: string): Promise<any[]> {
    // 1) Fetch assignments for this queue
    const { data: assignments, error: assignmentError } = await this.client
      .from('agent_queue_assignments')
      .select('id, agent_id, queue_id, priority, created_at')
      .eq('queue_id', queueId)
      .order('priority', { ascending: false });

    if (assignmentError) throw new RepositoryError('Failed to fetch queue agents', assignmentError);
    if (!assignments || assignments.length === 0) return [];

    // 2) Fetch corresponding agents to get user_id mapping (agents.id -> agents.user_id)
    const agentIds = [...new Set(assignments.map((a: any) => a.agent_id))];
    const { data: agents, error: agentsError } = await this.client
      .from('agents')
      .select('id, user_id, name, email, current_status')
      .in('id', agentIds);

    if (agentsError) throw new RepositoryError('Failed to fetch agent details', agentsError);

    const agentMap = new Map((agents || []).map((a: any) => [a.id, a]));

    // 3) Return assignments with agent_user_id for UI to correlate with app_users.id
    return assignments.map((a: any) => {
      const agent = agentMap.get(a.agent_id);
      return {
        ...a,
        agent_user_id: agent?.user_id || null,
        agent_name: agent?.name || null,
        agent_email: agent?.email || null,
        agent_status: agent?.current_status || null
      };
    });
  }

  /**
   * Get all queues for an agent
   */
  async getAgentQueues(agentId: string): Promise<any[]> {
    const { data, error } = await this.client
      .from('agent_queue_assignments')
      .select(`
        id,
        agent_id,
        queue_id,
        priority,
        created_at,
        call_queues (
          id,
          name,
          company_name,
          is_active
        )
      `)
      .eq('agent_id', agentId)
      .order('priority', { ascending: false });

    if (error) throw new RepositoryError('Failed to fetch agent queues', error);
    return data || [];
  }

  /**
   * Assign agent to queue
   */
  async assignAgentToQueue(
    agentId: string,
    queueId: string,
    priority: number = 50
  ): Promise<AgentQueueAssignment> {
    const { data, error } = await this.client
      .from('agent_queue_assignments')
      .insert({
        agent_id: agentId,
        queue_id: queueId,
        priority
      })
      .select()
      .single();

    if (error) throw new RepositoryError('Failed to assign agent to queue', error);
    return data as AgentQueueAssignment;
  }

  /**
   * Remove agent from queue
   */
  async removeAgentFromQueue(assignmentId: string): Promise<void> {
    const { error } = await this.client
      .from('agent_queue_assignments')
      .delete()
      .eq('id', assignmentId);

    if (error) throw new RepositoryError('Failed to remove agent from queue', error);
  }

  /**
   * Check if agent is assigned to queue
   */
  async isAgentInQueue(agentId: string, queueId: string): Promise<boolean> {
    const { data, error } = await this.client
      .from('agent_queue_assignments')
      .select('id')
      .eq('agent_id', agentId)
      .eq('queue_id', queueId)
      .maybeSingle();

    if (error) throw new RepositoryError('Failed to check agent assignment', error);
    return !!data;
  }

  // ==================== PHONE ASSIGNMENTS ====================

  /**
   * Get all phone assignments for a queue
   */
  async getQueuePhones(queueId: string): Promise<PhoneQueueAssignment[]> {
    const { data, error } = await this.client
      .from('phone_queue_assignments')
      .select('*')
      .eq('queue_id', queueId)
      .order('phone_number');

    if (error) throw new RepositoryError('Failed to fetch queue phones', error);
    return (data || []) as PhoneQueueAssignment[];
  }

  /**
   * Get queue for a phone number
   */
  async getPhoneQueue(phoneNumber: string): Promise<PhoneQueueAssignment | null> {
    const { data, error } = await this.client
      .from('phone_queue_assignments')
      .select('*')
      .eq('phone_number', phoneNumber)
      .eq('is_primary', true)
      .maybeSingle();

    if (error) throw new RepositoryError('Failed to fetch phone queue', error);
    return data as PhoneQueueAssignment | null;
  }

  /**
   * Assign phone number to queue
   */
  async assignPhoneToQueue(
    phoneNumber: string,
    queueId: string,
    isPrimary: boolean = true
  ): Promise<PhoneQueueAssignment> {
    const { data, error } = await this.client
      .from('phone_queue_assignments')
      .insert({
        phone_number: phoneNumber,
        queue_id: queueId,
        is_primary: isPrimary
      })
      .select()
      .single();

    if (error) throw new RepositoryError('Failed to assign phone to queue', error);
    return data as PhoneQueueAssignment;
  }

  /**
   * Remove phone assignment
   */
  async removePhoneFromQueue(assignmentId: string): Promise<void> {
    const { error } = await this.client
      .from('phone_queue_assignments')
      .delete()
      .eq('id', assignmentId);

    if (error) throw new RepositoryError('Failed to remove phone from queue', error);
  }

  /**
   * Get all phone assignments across all queues
   */
  async getAllPhoneAssignments(): Promise<PhoneQueueAssignment[]> {
    const { data, error } = await this.client
      .from('phone_queue_assignments')
      .select(`
        *,
        call_queues (
          id,
          name,
          company_name,
          is_active
        )
      `)
      .order('phone_number');

    if (error) throw new RepositoryError('Failed to fetch all phone assignments', error);
    return (data || []) as PhoneQueueAssignment[];
  }

  // ==================== AGENTS ====================

  /**
   * Get all call center agents
   */
  async getAllAgents(): Promise<CallCenterAgent[]> {
    // Query agents table and join with auth.users to get proper user information
    const { data, error } = await this.client
      .from('agents')
      .select(`
        id,
        user_id,
        name,
        email,
        phone_number,
        current_status,
        is_active,
        created_at,
        updated_at
      `)
      .eq('is_active', true)
      .order('name');

    if (error) throw new RepositoryError('Failed to fetch agents', error);
    
    // Transform agents to CallCenterAgent structure
    return (data || []).map(agent => ({
      id: agent.id,
      name: agent.name || 'Unknown',
      email: agent.email || '',
      phone: agent.phone_number || null,
      sip_username: null,
      current_status: agent.current_status || 'available',
      created_at: agent.created_at,
      updated_at: agent.updated_at
    })) as CallCenterAgent[];
  }

  /**
   * Get available agents (not busy)
   */
  async getAvailableAgents(): Promise<CallCenterAgent[]> {
    // Since app_users doesn't have status, return all users
    return this.getAllAgents();
  }

  // ==================== STATISTICS ====================

  /**
   * Get queue statistics for today
   */
  async getQueueStats(queueName: string): Promise<any> {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();

    // Get completed calls for today's stats
    const { data, error } = await this.client
      .from('calls')
      .select('status, started_at, ended_at, duration_seconds')
      .eq('queue_name', queueName)
      .gte('started_at', start.toISOString())
      .lte('started_at', end.toISOString());

    if (error) throw new RepositoryError('Failed to fetch queue stats', error);

    const calls = data || [];
    const totalCalls = calls.length;
    
    // Count answered calls (calls that have ended_at set and status is completed)
    const answered = calls.filter((c: any) => 
      c.ended_at !== null && c.status === 'completed'
    ).length;
    
    // Count abandoned calls
    const abandoned = calls.filter((c: any) => 
      c.status === 'canceled' || c.status === 'no-answer' || c.status === 'busy' || c.status === 'failed'
    ).length;

    // Calculate wait times (time from started_at to ended_at for non-null cases)
    const waitTimes = calls
      .filter((c: any) => c.started_at && c.ended_at)
      .map((c: any) => {
        const start = new Date(c.started_at).getTime();
        const end = new Date(c.ended_at).getTime();
        return Math.max(0, Math.round((end - start) / 1000));
      });
    
    const totalWait = waitTimes.reduce((sum: number, time: number) => sum + time, 0);
    const totalHandle = calls.reduce((sum: number, c: any) => sum + (c.duration_seconds || 0), 0);

    // Get active calls currently in queue
    const { data: activeCalls } = await this.client
      .from('calls')
      .select('started_at')
      .eq('queue_name', queueName)
      .is('ended_at', null);

    const currentCalls = activeCalls || [];
    const now = Date.now();
    
    // Calculate wait times for active calls
    const activeWaitTimes = currentCalls.map((c: any) => {
      if (!c.started_at) return 0;
      return Math.round((now - new Date(c.started_at).getTime()) / 1000);
    });

    return {
      total_calls: totalCalls,
      answered,
      abandoned,
      // Historical averages over completed calls today
      avg_wait_time: waitTimes.length > 0 ? Math.round(totalWait / waitTimes.length) : 0,
      avg_handle_time: totalCalls > 0 ? Math.round(totalHandle / totalCalls) : 0,
      // New: historical maximum wait among completed calls today
      max_wait_seconds_today: waitTimes.length > 0 ? Math.max(...waitTimes) : 0,
      // Service metrics and live queue state
      service_level: totalCalls > 0 ? (answered / totalCalls) * 100 : 0,
      calls_in_queue: currentCalls.length,
      // Live wait stats for active callers right now
      longest_wait_seconds: activeWaitTimes.length > 0 ? Math.max(...activeWaitTimes) : 0,
      average_wait_seconds: activeWaitTimes.length > 0 
        ? Math.round(activeWaitTimes.reduce((a, b) => a + b, 0) / activeWaitTimes.length) 
        : 0
    };
  }
}

// Export singleton instance
export const queueRepository = new QueueRepository();
